<template>
  <StatusCircle
    :model-value="action.status"
    :disable="!showPopup"
    :loading="loading"
    :show-act-and-archive="showActAndArchive"
    :show-only-archive-text="action.status === SQDCActionStatus.ACT"
    :disable-act-and-archive-reason="disableActAndArchiveReason"
    @update:model-value="updateActionStatus"
    @archive-event="updateStatusAndArchiveEvent"
  >
    <template v-if="$slots.default" #default="{ resetTimer, stopTimer }">
      <slot :reset-timer="resetTimer" :stop-timer="stopTimer"></slot>
    </template>
  </StatusCircle>
</template>

<script setup lang="ts">
import { useSQDCEventArchiveWithSpider } from "@/modules/sqdc/helper/useSQDCEventArchiveWithSpider";
import { type PropType, computed } from "vue";
import { DateTime } from "luxon";
import { useStore } from "vuex";
import {
  PreventArchivingType,
  SQDCActionRevisionFieldType,
  SQDCActionStatus,
  useSQDCActionStatusUpdateMutation,
  SQDCEventRevisionFieldType,
} from "@/graphql";
import { useEventArchive } from "@/components/event-action/event/composables/eventArchive";
import type { EABEventActionType, EABEventType } from "./EventActionHelper";
import StatusCircle from "./StatusCircle.vue";
const props = defineProps({
  action: {
    type: Object as PropType<EABEventActionType>,
    required: true,
  },
  event: {
    type: Object as PropType<EABEventType>,
    required: false,
    default: undefined,
  },
  disablePopup: {
    type: Boolean,
    default: false,
  },
});
const store = useStore();
const { mutate: mutateActionStatus, loading } = useSQDCActionStatusUpdateMutation({});
// TODO: needs a better logic
const { archivingBlockedReason, archivingBlockedEnum, blockingTask } = useEventArchive(
  computed(() => ({
    actions: (props.event?.ActionEventConnection ?? [])
      .map((connection) => ({
        id: connection.action?.id,
        preventsArchiving: connection.action?.status !== SQDCActionStatus.ACT,
      }))
      .filter((action) => action.id != null),
  })),
);
const showActAndArchive = computed(() => props.event != null);
const disableActAndArchiveReason = computed(() => {
  return archivingBlockedEnum.value != null &&
    !(
      archivingBlockedEnum.value === PreventArchivingType.ACTION_STATUS &&
      blockingTask.value.length === 1 &&
      blockingTask.value[0].id === props.action.id
    )
    ? archivingBlockedReason.value
    : undefined;
});
async function updateStatusAndArchiveEvent() {
  if (!showPopup.value) return;
  await updateActionStatus(SQDCActionStatus.ACT);
  if (props.event == null) {
    return;
  }
  const changedById = store.getters["login/userId"] as number;
  const changedByPreferredName = store.getters["login/preferredName"] as string;
  await archiveEvent(
    {
      eventId: props.event.id,
      archive: true,
    },
    {
      optimisticResponse: {
        SQDCEventArchive: {
          __typename: "SQDCEvent",
          id: props.event.id,
          deletedAt: DateTime.now().toISO(),
          updatedAt: DateTime.now().toISO(),
          SQDCEventRevisions: [
            {
              __typename: "SQDCEventRevision",
              id: -1,
              changes: [
                {
                  __typename: "SQDCEventRevisionChange",
                  fieldType: SQDCEventRevisionFieldType.ARCHIVE,
                  fieldId: null as any,
                  oldValue: false,
                  newValue: true,
                  includeInLast: true,
                },
              ],
              changedById,
              changedBy: {
                __typename: "User",
                id: changedById,
                preferredName: changedByPreferredName,
              },
              createdAt: DateTime.now().toISO(),
              comment: null as any,
            },
            ...props.event.SQDCEventRevisions.slice(-4),
          ],
        },
      },
    },
  );
}
const { mutate: archiveEvent } = useSQDCEventArchiveWithSpider();
async function updateActionStatus(status: SQDCActionStatus) {
  if (!showPopup.value) return;
  if (status === props.action.status) {
    return;
  }
  const commentatorId = store.getters["taskComment/getTaskCommentatorId"];
  await mutateActionStatus(
    {
      actionId: props.action.id,
      status,
      userId: commentatorId,
    },
    {
      optimisticResponse: {
        SQDCActionStatusUpdate: {
          __typename: props.action.__typename,
          id: props.action.id,
          updatedAt: DateTime.now().toISO(),
          status,
          // preventsArchiving: status !== TaskStatus.act,
          deletedAt: props.action.deletedAt,
          SQDCActionRevisions: [
            {
              __typename: "SQDCActionRevision",
              id: -1,
              changes: [
                {
                  __typename: "SQDCActionRevisionChange",
                  fieldType: SQDCActionRevisionFieldType.STATUS,
                  fieldId: null as any,
                  oldValue: props.action.status,
                  newValue: status,
                  includeInLast: true,
                },
              ],
              changedById: commentatorId,
              changedBy: null as never,
              createdAt: DateTime.now().toISO(),
              comment: null as any,
            },
            ...props.action.SQDCActionRevisions.slice(-4),
          ],
        },
      },
    },
  );
}
const showPopup = computed(() => props.action.deletedAt === null && !props.disablePopup);
</script>

<style lang="scss" scoped></style>
